import { getPrismicLocale, Locales } from "@/lib/i18n";
import { getAllByIDs, getAllByUids, getByID } from "@/lib/prismic";
import {
  InspirationDocument,
  InspirationDocumentDataProductsItem,
  ProductDocument,
  Simplify,
  TablesDocument,
  TablesDocumentDataRelatedProductsItem,
  TablesDocumentDataRelatedTablesItem,
} from "@/prismicio-types";
import { InspirationsWithProducts } from "@/types";
import { GroupField, isFilled } from "@prismicio/client";

export const resolveProducts = async (
  products: GroupField<Simplify<InspirationDocumentDataProductsItem>>,
  locale: Locales
) => {
  const resolvedProducts = await Promise.all(
    products.map(async ({ product }) => {
      if (isFilled.contentRelationship(product)) {
        return await getByID<ProductDocument>(product.id, { lang: product.lang });
      }
    })
  );

  const initialProducts = resolvedProducts.filter((product): product is ProductDocument => !!product);

  const expandedProducts = await Promise.all(
    initialProducts.map(async (product) => {
      if (product.data.productCollection) {
        const collectionProductIds = product.data.products
          .map((item) => item.product)
          .filter(isFilled.contentRelationship)
          .map((docLink) => docLink.id)
          .filter((id): id is string => typeof id === "string");

        const collectionProducts = (await getAllByIDs(collectionProductIds, {
          lang: product.lang,
        })) as ProductDocument[] | null;

        return [product, ...(collectionProducts || [])];
      }

      return [product];
    })
  );

  const filteredProducts = expandedProducts.flat().filter((product) => !product.data.productCollection);

  if (locale === "fr") return filteredProducts;

  const frenchProductUIDs = filteredProducts
    .map(
      (product) => product.alternate_languages.find((alt) => alt.lang === getPrismicLocale("fr"))?.uid || product.uid
    )
    .filter(Boolean);

  const frenchProducts = (await getAllByUids("product", frenchProductUIDs, "fr")) as ProductDocument[] | null;

  return filteredProducts
    ?.map((localeProduct) => {
      const frenchEquivalent = frenchProducts?.find(
        (frenchProduct) =>
          frenchProduct.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid ===
          localeProduct.uid
      );
      if (frenchEquivalent) {
        return {
          ...localeProduct,
          data: {
            ...localeProduct.data,
            images: frenchEquivalent.data.images,
            featuredImage: frenchEquivalent.data.featuredImage,
            productCollection: frenchEquivalent.data.productCollection,
            image_full_size_card: frenchEquivalent.data.image_full_size_card,
          },
        };
      }
      return undefined;
    })
    .filter(Boolean) as ProductDocument[];
};

export const resolveInspirationsWithProducts = async (
  inspirations: InspirationDocument[],
  locale: Locales
): Promise<InspirationsWithProducts[]> => {
  const inspirationsWithProducts = await Promise.all(
    inspirations.map(async (inspiration) => {
      const products = await resolveProducts(inspiration.data.products, locale);
      return {
        ...inspiration,
        resolvedProducts: products,
      };
    })
  );
  return inspirationsWithProducts;
};

export const getRelatedTables = async (
  relatedTables: GroupField<Simplify<TablesDocumentDataRelatedTablesItem>>,
  locale: Locales
) => {
  const resolvedTables = await Promise.all(
    relatedTables.map(async ({ table }) => {
      if (isFilled.contentRelationship(table)) {
        return await getByID<TablesDocument>(table.id);
      }
    })
  );

  const filteredTables = resolvedTables.filter((table): table is TablesDocument => !!table);

  if (locale === "fr") return filteredTables;

  const localeTableUids = filteredTables
    .map((table) => table.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid || table.uid)
    .filter(Boolean);

  const localeTables = (await getAllByUids("tables", localeTableUids, locale)) as TablesDocument[];

  return localeTables;
};

export const resolveTableProducts = async (
  products: GroupField<Simplify<TablesDocumentDataRelatedProductsItem>>,
  locale: Locales
) => {
  const frenchProducts = await Promise.all(
    products.map(async ({ product }) => {
      if (isFilled.contentRelationship(product)) {
        return await getByID<ProductDocument>(product.id, { lang: product.lang });
      }
    })
  );

  const filteredProducts = frenchProducts.filter((product): product is ProductDocument => !!product);

  if (locale === "fr") return filteredProducts;

  const localeProductUIDs = frenchProducts
    .map(
      (product) =>
        product?.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid || product?.uid || ""
    )
    .filter(Boolean);

  const localeProducts = (await getAllByUids("product", localeProductUIDs, locale)) as ProductDocument[] | null;

  return localeProducts
    ?.map((localeProduct) => {
      const frenchEquivalent =
        filteredProducts?.find(
          (frenchProduct) =>
            frenchProduct?.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid ===
            localeProduct.uid
        ) || localeProduct;
      if (frenchEquivalent) {
        return {
          ...localeProduct,
          data: {
            ...localeProduct.data,
            images: frenchEquivalent.data.images,
            featuredImage: frenchEquivalent.data.featuredImage,
            productCollection: frenchEquivalent.data.productCollection,
            image_full_size_card: frenchEquivalent.data.image_full_size_card,
          },
        };
      }
      return undefined;
    })
    .filter(Boolean) as ProductDocument[];
};
